import React from "react";
import PropTypes from "prop-types";
import {I18n} from "aws-amplify/utils";
import {Box, InputAdornment, List, Popper, TextField} from "@mui/material";
import {Clear, Search} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getIconForNode} from "../../../Models/Roles";
import {filterPayStations} from "../SearchBar/PaystationFilteringLogic";
import {getDetailedAccounts} from "../../../graphql/queries";
import GraphQlTool from "../../../Utils/GraphQlTool";
import './BoxSelector.css';

const defaultName = "MONNAYEUR";
let selectorInstance;

/**
 * @typedef {Object} BoxSelectorProps
 * @property {import('../../../Models/DomainTypes').Box[]} boxes Boxes available for selection.
 * @property {import('../../../Models/DomainTypes').PayStation[]} payStations
 * @property {import('../../../Utils/UserDataManager').default | null} [user]
 * @property {{ graphql: Function }} API
 * @property {number} selectedBoxID Index of the selected box in `boxes`, not its domain ID.
 * @property {string} [placeholder]
 * @property {boolean} [isDarkStyle]
 * @property {boolean} [showIcon]
 * @property {(event: React.MouseEvent, boxIndex: number) => void} [onChange]
 */

/**
 * Recherche et sélectionne un boîtier parmi les installations disponibles.
 * @extends {React.Component<BoxSelectorProps>}
 */
export default class BoxSelector extends React.Component {
	static defaultProps = {
		selectedBoxID: 0,
		placeholder: '',
		isDarkStyle: false,
		showIcon: false,
		onChange: () => {}
	};

	static propTypes = {
		selectedBoxID: PropTypes.number.isRequired,
		placeholder: PropTypes.string.isRequired,
		isDarkStyle: PropTypes.bool.isRequired,
		showIcon: PropTypes.bool,
		onChange: PropTypes.func
	};

	constructor(props) {
		super(props);
		selectorInstance = this;
		this.containerReference = React.createRef();
		this.listReference = React.createRef();
		this.inputReference = React.createRef();
		this.state = {
			searchValue: this.getFirstTruthyName( this.props.selectedBoxID ),
			highlightedIndex: -1,
			isOpened: false,
			filteredBoxes: []
		};
	}

	loadAccounts() {
		if( this.props.API && this.props.user?.observer?.currentUser?.accountId ) {
			this.props.API
				.graphql( { query: getDetailedAccounts, variables: { accountId: this.props.user?.observer?.currentUser?.accountId }} )
				.then( returned => {
					const tool = new GraphQlTool( 'getDetailedAccounts' , returned );
					this.setState( { accounts: tool.extract() } );
				})
				.catch( ( error ) => {
					console.error( "error" , error );
				})
		}
	}

	/** @param {number} index Index in `this.props.boxes`. */
	getFirstTruthyName( index ) {
		const optionalSearch =
			this.props.boxes[index]?.informations?.attributes?.name.toUpperCase().includes( defaultName ) ?
				this.props.boxes[index]?.extraData : '';

		return  optionalSearch ||
			this.props.boxes[index]?.informations?.attributes?.name ||
			this.props.boxes[index]?.name ||
			'';
	}

	handleClickOutOfTheZone( event ) {
		const clickedInContainer = selectorInstance.containerReference?.current?.contains( event.target );
		const clickedInList = selectorInstance.listReference?.current?.contains( event.target );
		if ( ! clickedInContainer && ! clickedInList) {
			selectorInstance.setState({ isOpened: false, highlightedIndex: -1 });
		}
	}

	clearAllFiltersIfNoSearch() {
		if( selectorInstance.state.searchValue?.trim() === '' &&
			selectorInstance.state.filteredBoxes !== selectorInstance.props.boxes ) {
			selectorInstance.setState( {
				filteredBoxes: selectorInstance.props.boxes,
				isOpened: false,
				highlightedIndex: -1
			} , () => { selectorInstance.inputReference?.current?.focus() } );
			return true;
		}
		return false;
	}

	/**
	 * @param {React.MouseEvent} event
	 * @param {number} boxIndex Index in `this.props.boxes`.
	 */
	handleUserSelection( event , boxIndex ) {
		if( selectorInstance.props.onChange )
			selectorInstance.props.onChange( event , boxIndex );

		selectorInstance.setState( {
			searchValue: selectorInstance.getFirstTruthyName( boxIndex ),
			highlightedIndex: -1,
			isOpened: false
		} );
	}

	clearSearch( event ) {
		selectorInstance.setState( { searchValue: '' } , () => { selectorInstance.inputReference?.current?.focus() } );
	}

	/** @param {import('../../../Models/DomainTypes').Box | null | undefined} box */
	extractBoxLabel( box ) {
		if( box?.name ) {
			const customName = box?.informations?.attributes?.name;
			return ( customName ) ? customName : box?.name;
		}
		return I18n.get("error");
	}

	/** @param {import('../../../Models/DomainTypes').Box | null | undefined} box */
	extractBoxExtraLabel( box ) {
		return ( box?.extraData ) ? box.extraData : "";
	}

	componentDidMount() {
		document.addEventListener( 'pointerdown', this.handleClickOutOfTheZone );
	}

	componentWillUnmount() {
		document.removeEventListener( 'pointerdown', this.handleClickOutOfTheZone );
	}

	renderSearchBar() {
		return (
			<TextField
				key={`box-selector-text-field`}
				ref={ selectorInstance.containerReference }
				inputRef={ selectorInstance.inputReference }
				label={ I18n.get('DEFAULT_FILTER') }
				className="searchbar-textField"
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Search />
						</InputAdornment>
					),
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								size="small"
								edge="end"
								onClick={ selectorInstance.clearSearch }
							>
								<Clear fontSize="small" />
							</IconButton>
						</InputAdornment>
					),
				}}
				size={ "medium" }
				variant={ "outlined" }
				placeholder={ selectorInstance.props.placeholder }
				value={ selectorInstance.state.searchValue }
				onChange={ event => selectorInstance.setState( { searchValue: event.target.value } ) }
				onFocus={ () => selectorInstance.setState( { isOpened: true } ) }
				autoComplete={ "off" }
			/>
		);
	}

	renderFiltered() {
		const popperPlacement = "bottom-start";
		const paperElevation = 3;
		const theme = selectorInstance.props.isDarkStyle ? "dark" : "";
		const classes = {
			popper: `filtered-popper`,
			paper: `filtered-popper`,
			list: `filtered-list`,
			listItem: `filtered-list-item ${theme}`,
			listItemIcon: `filtered-list-item-icon ${theme}`,
			boxTitle: `filtered-box-title ${theme}`,
			boxSubTitle: `filtered-box-subtitle ${theme}`
		};

		if( ! selectorInstance.containerReference.current ) return null;

		return (
			<Popper
				open={ selectorInstance.state.isOpened }
				anchorEl={ selectorInstance.containerReference.current }
				placement={ popperPlacement }
				className={ classes.popper } style={{ zIndex: 1500 }} >
				<Paper
					elevation={ paperElevation }
					className={ classes.paper } >
					<List
						ref={ selectorInstance.listReference }
						dense
						className={ classes.list }
						autoFocus={false}
					>
						{selectorInstance.renderBoxes( classes )}
					</List>
				</Paper>
			</Popper>
		);
	}

	renderBoxes( classes ) {
		return(
			<>
				{ selectorInstance.state.filteredBoxes?.map( ( box, index ) => (
					<ListItemButton
						className={ classes.listItem }
						key={ box.id }
						selected={ index === selectorInstance.state.highlightedIndex }
						autoFocus={false}
						onClick={ evt => selectorInstance.handleUserSelection( evt , selectorInstance.props.boxes.indexOf( box ) ) }
					>
						{ selectorInstance.renderOptionalIcon( classes , box ) }
						<Box>
							<div className={ classes.boxTitle }>{ selectorInstance.extractBoxLabel( box ) }</div>
							<div className={ classes.boxSubTitle }>{ selectorInstance.extractBoxExtraLabel( box ) }</div>
						</Box>
					</ListItemButton>
				))}
			</>
		);
	}

	/** @param {object} classes @param {import('../../../Models/DomainTypes').Box} box */
	renderOptionalIcon( classes , box ) {
		if( this.props.showIcon ) {
			return (
				<ListItemIcon className={classes.listItemIcon}>
					<FontAwesomeIcon icon={ getIconForNode( box ) }/>
				</ListItemIcon>
			);
		}
		return null;
	}

	render() {
		if( ! this.state.accounts ) {
			this.loadAccounts();
			return null;
		}

		if( this.clearAllFiltersIfNoSearch() ) {
			return null;
		} else {
			const filtered = filterPayStations( this.props.boxes , this.state.searchValue , this.state.accounts );
			const isFocused = document.activeElement === this.inputReference.current;
			if( ( this.state.searchValue && isFocused ) && this.state.filteredBoxes.length !== filtered.length ) {
				this.setState( {
					filteredBoxes: filtered,
					isOpened: filtered.length > 0 /*&& isFocused*/,
					highlightedIndex: -1
				} , () => { this.inputReference?.current?.focus() } );
				return null;
			} else {
				return (
					<div>
						{ this.renderSearchBar() }
						{ this.renderFiltered() }
					</div>
				);
			}
		}
	}
}
